/**
 * Client-side reservation/contact tracking.
 * Fire-and-forget inserts into `reservation_events`. Never throws.
 */
import { supabase } from "@/integrations/supabase/client";

export type ReservationEventType =
  | "page_view"
  | "phone_click"
  | "email_click"
  | "maps_click"
  | "whatsapp_click"
  | "menu_view"
  | "contact_form_submit"
  | "reservation_cta_click"
  | "event_request_submit"
  | "popup_view"
  | "popup_click";

const SESSION_KEY = "lpc:rt:sid";

function getSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid =
        (crypto.randomUUID && crypto.randomUUID()) ||
        Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "anon";
  }
}

function getLanguage(): string {
  try {
    return (document.documentElement.lang || navigator.language || "fr").slice(0, 8);
  } catch {
    return "fr";
  }
}

export function trackEvent(
  type: ReservationEventType,
  options: { section?: string; metadata?: Record<string, unknown> } = {}
): void {
  // Respect cookie consent: skip if user explicitly declined analytics.
  try {
    const consent = localStorage.getItem("lpc:cookie-consent");
    if (consent === "denied") return;
  } catch {
    /* ignore */
  }

  // Skip on admin routes (internal traffic shouldn't pollute analytics).
  try {
    if (window.location.pathname.startsWith("/admin")) return;
  } catch {
    /* ignore */
  }

  const payload = {
    event_type: type,
    source_page: typeof window !== "undefined" ? window.location.pathname : null,
    source_section: options.section ?? null,
    session_id: getSessionId(),
    user_agent:
      typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
    referrer:
      typeof document !== "undefined" && document.referrer
        ? document.referrer.slice(0, 500)
        : null,
    language: getLanguage(),
    metadata: (options.metadata ?? {}) as never,
  };

  // Fire-and-forget; swallow errors so tracking never breaks the UI.
  void supabase
    .from("reservation_events")
    .insert([payload])
    .then(({ error }) => {
      if (error && import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug("[tracking] insert failed:", error.message);
      }
    });
}
